import { useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { SizedResponsiveContainer } from '@/shared/components/charts/SizedResponsiveContainer';

import { useAuth } from '@/modules/auth/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { formatMoney } from '@/shared/utils/money';
import { useAnalyticsDashboard } from '../hooks/useAnalyticsDashboard';
import { formatCountRatioFraction, formatCountRatioPercent } from '../utils/ratio';

function KpiCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useAnalyticsDashboard();

  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [user, isSuperAdmin, navigate]);

  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  const revenueChart = useMemo(() => {
    if (!data) return [];
    const byDay = new Map<string, number>();
    for (const row of data.time_series.revenue_daily) {
      if (row.currency !== data.payments.revenue_last_30_days.currency) continue;
      byDay.set(row.day, (byDay.get(row.day) ?? 0) + row.revenue_minor);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, revenue_minor]) => ({ day, revenue_minor }));
  }, [data]);

  const subsChart = useMemo(() => {
    if (!data) return [];
    return data.time_series.subscriptions_daily.map((row) => ({
      day: row.day,
      new_count: row.new_count,
      cancelled_count: row.cancelled_count,
    }));
  }, [data]);

  if (!user || !isSuperAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-20 space-y-3">
        <p className="text-destructive">{t('analytics.loadError')}</p>
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() => void refetch()}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const currency = data.payments.revenue_last_30_days.currency;

  return (
    <div className="w-full min-w-0 space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('analytics.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('analytics.refreshedAt', {
              time: new Date(data.refreshed_at).toLocaleString(locale === 'ar' ? 'ar-SA' : 'en-US'),
            })}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {data.range.from} → {data.range.to}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={t('analytics.kpi.activeSubs')}
          value={String(data.subscriptions.active_total)}
        />
        <KpiCard
          title={t('analytics.kpi.mrr')}
          value={formatMoney(data.subscriptions.mrr.amount_minor, currency, locale)}
        />
        <KpiCard
          title={t('analytics.kpi.revenue30d')}
          value={formatMoney(data.payments.revenue_last_30_days.amount_minor, currency, locale)}
        />
        <KpiCard
          title={t('analytics.kpi.paymentSuccess')}
          value={formatCountRatioPercent(data.payments.success_vs_failed)}
          hint={formatCountRatioFraction(data.payments.success_vs_failed)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={t('analytics.kpi.new30d')}
          value={String(data.subscriptions.new_last_30_days)}
        />
        <KpiCard
          title={t('analytics.kpi.cancellations30d')}
          value={String(data.subscriptions.cancellations_last_30_days)}
        />
        <KpiCard
          title={t('analytics.kpi.trialConversion')}
          value={formatCountRatioPercent(data.subscriptions.trial_conversion)}
          hint={formatCountRatioFraction(data.subscriptions.trial_conversion)}
        />
        <KpiCard
          title={t('analytics.kpi.avgPayment')}
          value={formatMoney(data.payments.average_payment.amount_minor, currency, locale)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={t('analytics.kpi.tenants')}
          value={String(data.operational.total_tenants)}
        />
        <KpiCard title={t('analytics.kpi.users')} value={String(data.operational.total_users)} />
        <KpiCard
          title={t('analytics.kpi.webhookDelivery')}
          value={formatCountRatioPercent(data.operational.webhook_delivery)}
        />
        <KpiCard
          title={t('analytics.kpi.errorRate24h')}
          value={formatCountRatioPercent(data.operational.error_rate_24h)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.charts.revenue')}</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <SizedResponsiveContainer height={288} className="w-full min-w-0">
              <LineChart data={revenueChart}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => formatMoney(v, currency, locale)}
                />
                <Tooltip
                  formatter={(value) => formatMoney(Number(value ?? 0), currency, locale)}
                  labelFormatter={(label) => String(label)}
                />
                <Line
                  type="monotone"
                  dataKey="revenue_minor"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </SizedResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.charts.subscriptions')}</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <SizedResponsiveContainer height={288} className="w-full min-w-0">
              <BarChart data={subsChart}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="new_count"
                  name={t('analytics.charts.new')}
                  fill="hsl(var(--primary))"
                />
                <Bar
                  dataKey="cancelled_count"
                  name={t('analytics.charts.cancelled')}
                  fill="hsl(var(--destructive))"
                />
              </BarChart>
            </SizedResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.activeByProduct')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-start px-4 py-2 font-medium">{t('analytics.product')}</th>
                  <th className="text-end px-4 py-2 font-medium">{t('analytics.activeCount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.active_by_product.map((row) => (
                  <tr key={row.product_id} className="border-t">
                    <td className="px-4 py-2 font-mono">{row.product_slug}</td>
                    <td className="px-4 py-2 text-end tabular-nums">{row.active_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
